import { FC, PropsWithChildren, useRef, useEffect } from 'react';
import { DealerDetailsDisplayConfig, DealerDetailsProps } from '../dealer.types';
import { OpeningHoursAccordion } from './dealer-opening-hours-accordion';
import { DealerNpsForm } from './dealer-nps-form';
import { DealerDistanceLabel } from './dealer-distance-label';
import { DealerPreferenceActionBar } from './dealer-preference-action-bar';
import { useDealerMapContext } from '../context/dealer-map-context';
import { cx } from '@emotion/css';
import { DealerActionButtons } from './dealer-action-buttons';
import { DealerPhoneNumberLabel } from './dealer-phone-number-label';
import { useDealerLocations } from '../hooks/use-dealer-locations.hook';
import { DealerDetailsBackButton } from './dealer-details-back-button';
import { DealerOpeningHours as OpeningHours } from './dealer-opening-hours';
import { useTranslation } from 'next-i18next';
import { DSHeading } from '@stihl-design-system/components';

export const DealerDetails: FC<PropsWithChildren<DealerDetailsProps>> = ({
  displayConfig,
  layout = 'VERTICAL',
  className,
  isStandalone = true,
  children,
  childrenClassName,
}) => {
  useDealerLocations();
  const { selectedDealer } = useDealerMapContext();
  const { t } = useTranslation('shared');
  const backButtonRef = useRef<HTMLButtonElement>(null);

  const config = {
    preferencesEnabled: false,
    preferencesEditable: false,
    addressEnabled: true,
    phoneNumberEnabled: false,
    distanceEnabled: false,
    planRouteButton: false,
    writeEmailButton: false,
    visitWebsiteButton: false,
    openingHoursEnabled: false,
    openingHoursInitiallyExpanded: false,
    npsEnabled: false,
    openingHoursHeadline: false,
    ...displayConfig,
  } as DealerDetailsDisplayConfig;

  useEffect(() => {
    if (backButtonRef.current) {
      backButtonRef.current.focus();
    }
  }, []);

  if (!selectedDealer) {
    return null;
  }

  const dealerHasOpeningHours = Object.values(selectedDealer?.openingHours).some((hours: string[]) => hours.length > 0);

  return (
    <div
      className={cx(
        'z-10 h-full p-6',
        'focus-visible:outline-offset-[-3px] focus-visible:outline-[#252525] focus-visible:outline-[2px] focus-visible:outline',
        !isStandalone && 'min-h-[50vh] overflow-y-auto   bg-primary-white pl-12',
        className
      )}
      data-testid="dealer-details"
    >
      <DealerDetailsBackButton ref={backButtonRef} isStandalone={isStandalone} />
      <div className={cx('flex flex-col space-y-8', layout === 'HORIZONTAL' && 'tablet:flex-row tablet:space-y-0')}>
        <section
          className={cx(
            'flex',
            layout === 'HORIZONTAL' ? 'flex-1 flex-col gap-8 tablet:flex-row tablet:gap-12' : 'basis-full flex-col gap-4'
          )}
        >
          <div
            className={cx(
              layout === 'COMPACT' ? 'items-center justify-between space-y-6 tablet:flex tablet:space-x-4 tablet:space-y-0' : 'space-y-4'
            )}
          >
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className={cx(
                layout === 'COMPACT' ? 'flex-1 items-center space-y-2 tablet:flex tablet:space-x-4 tablet:space-y-0' : 'space-y-4'
              )}
            >
              <DSHeading>
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: selectedDealer?.description }}
                />
              </DSHeading>
              {config.preferencesEnabled && (
                <DealerPreferenceActionBar
                  dealer={selectedDealer}
                  editable={config.preferencesEditable}
                  shouldDeselectDealerAfterRemoval={!config?.distanceEnabled}
                  showInline={true}
                />
              )}
            </div>
            <div className={cx(layout === 'COMPACT' ? 'space-y-6 tablet:flex tablet:space-x-4 tablet:space-y-0' : 'space-y-4')}>
              {config.addressEnabled && (
                <div
                  className="stihl-text-body-default-reg"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: selectedDealer?.location.formatted?.replace('\n', ', ') }}
                />
              )}
              {(config.phoneNumberEnabled || config.distanceEnabled) && (
                <div className="flex space-x-6">
                  {config.phoneNumberEnabled && <DealerPhoneNumberLabel phoneNumber={selectedDealer?.contact.landlinePhone} />}
                  {config.distanceEnabled && <DealerDistanceLabel distance={selectedDealer?.location.distanceMeters} />}
                </div>
              )}
              {(config?.planRouteButton || config?.visitWebsiteButton || config?.writeEmailButton) && (
                <DealerActionButtons dealer={selectedDealer} displayConfig={config} />
              )}
            </div>
            {children && <div className={cx(childrenClassName)}>{children}</div>}
          </div>
        </section>

        {config.openingHoursEnabled && dealerHasOpeningHours && (
          <div className={cx(layout !== 'HORIZONTAL' && 'basis-full')}>
            {config.openingHoursHeadline ? (
              <>
                <div className="my-1 stihl-text-h5-mobile">{t('shared_dealers_headline_openingHours')}</div>
                <OpeningHours dealer={selectedDealer} dataTestId="dealer-details_opening-hours-details" />
              </>
            ) : (
              <OpeningHoursAccordion dealer={selectedDealer} openingHoursInitiallyExpanded={config.openingHoursInitiallyExpanded} />
            )}
          </div>
        )}
        <DealerNpsForm enabled={!!config.npsEnabled} />
      </div>
    </div>
  );
};
